import { supabase } from '../lib/supabase';
import emailService from './emailService';

/**
 * Price Alert Service for managing cryptocurrency price alerts
 */
class PriceAlertService {
  /**
   * Create a new price alert
   */
  async createPriceAlert(userId, alertData) {
    try {
      const { data, error } = await supabase?.from('price_alerts')?.insert({
          user_id: userId,
          crypto_symbol: alertData?.cryptoSymbol,
          crypto_name: alertData?.cryptoName,
          condition: alertData?.condition,
          target_price: alertData?.targetPrice,
          current_price: alertData?.currentPrice,
          status: 'active'
        })?.select()?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Failed to create price alert:', error);
      return { data: null, error: error?.message };
    }
  }

  /**
   * Get user price alerts
   */
  async getUserPriceAlerts(userId) {
    try {
      const { data, error } = await supabase?.from('price_alerts')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Failed to fetch price alerts:', error);
      return { data: null, error: error?.message };
    }
  }

  /**
   * Trigger price alert (when price condition is met)
   */
  async triggerPriceAlert(alertId, currentPrice) {
    try {
      // Get alert details
      const { data: alert, error: fetchError } = await supabase?.from('price_alerts')?.select('*')?.eq('id', alertId)?.single();

      if (fetchError) throw fetchError;

      // Update alert status
      const { data, error } = await supabase?.from('price_alerts')?.update({ 
          status: 'triggered',
          current_price: currentPrice,
          triggered_at: new Date()?.toISOString()
        })?.eq('id', alertId)?.select()?.single();

      if (error) throw error;

      // Send price alert email
      if (alert) {
        emailService?.sendPriceAlert(alert?.user_id, {
          cryptoSymbol: alert?.crypto_symbol,
          cryptoName: alert?.crypto_name,
          condition: alert?.condition,
          targetPrice: alert?.target_price,
          currentPrice: currentPrice
        });
      }

      return { data, error: null };
    } catch (error) {
      console.error('Failed to trigger price alert:', error);
      return { data: null, error: error?.message };
    }
  }

  /**
   * Delete price alert
   */
  async deletePriceAlert(alertId) {
    try {
      const { error } = await supabase?.from('price_alerts')?.delete()?.eq('id', alertId);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      console.error('Failed to delete price alert:', error);
      return { error: error?.message };
    }
  }

  /**
   * Toggle price alert status
   */
  async togglePriceAlert(alertId, status) {
    try {
      const { data, error } = await supabase?.from('price_alerts')?.update({ status: status })?.eq('id', alertId)?.select()?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Failed to toggle price alert:', error);
      return { data: null, error: error?.message };
    }
  }
}

export default new PriceAlertService();